/**
 * Equity / peak path — portfolio value vs running peak from risk-metrics drawdown series.
 */

import React, { useMemo } from "react";
import { CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts";
import { Box, Skeleton, Text } from "@chakra-ui/react";
import { useTheme } from "next-themes";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import {
  STATS_CHART_TOOLTIP_TITLE_CLASS,
  STATISTICS_NO_DATA_TIMEFRAME_MESSAGE,
  StatisticsChartTooltipRow,
  statsChartTooltipContentClassName,
} from "./statisticsChartTooltip";
import type { RiskMetricsResponse } from "../../../../types/trade";
import { useDisplayTimeZone } from "../../../../contexts/DisplayTimezoneContext";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { formatDisplayCurrency } from "../../../../utils/formatDisplayCurrency";
import {
  formatTradeDateOnlyForDisplay,
  formatYmdBucketShortInZone,
  shouldIncludeYearOnChartAxis,
} from "../../../../utils/displayDateTime";
import { useStatisticsWidgetErrorGate } from "../../../../hooks/useStatisticsWidgetErrorGate";
import { StatisticsInlineError } from "../../../../components/errors/StatisticsInlineError";
import {
  StatisticsCardHeadline,
  STATS_FONT_FAMILY,
  useStatsCardHeadlineColor,
} from "./statisticsCardHeadline";
import {
  statisticsShadcnBarChartAreaClassName,
  statisticsShadcnCardClassName,
  statisticsShadcnCardContentClassName,
} from "./statisticsShadcnCardShell";
import {
  buildEquityPeakSeries,
  type EquityPeakChartPoint,
} from "./drawdownSeriesMetrics";

const EQUITY_LINE_COLOR = "#38BBD1";
const PEAK_LINE_COLOR = "#6b7280"; // gray-500

const chartConfig = {
  equity: {
    label: "Equity",
    color: EQUITY_LINE_COLOR,
  },
  peak: {
    label: "Peak",
    color: PEAK_LINE_COLOR,
  },
} satisfies ChartConfig;

export interface EquityPeakPathChartProps {
  drawdownData?: RiskMetricsResponse["drawdown_data"];
  loading?: boolean;
  error?: string | null;
  errorAction?: string | null;
  /** Chart only — no header or card chrome. */
  compact?: boolean;
  /** Toggle header + chart without outer Card (e.g. comparison period boxes). */
  embedded?: boolean;
  /** Stretch card and chart to fill a matched-height row. */
  fillHeight?: boolean;
  title?: string;
  description?: string;
}

const EquityPeakPathChart: React.FC<EquityPeakPathChartProps> = ({
  drawdownData = [],
  loading = false,
  error = null,
  errorAction = null,
  compact = false,
  embedded = false,
  fillHeight = false,
  title = "Equity / Peak Path",
  description = "Portfolio value versus its running peak over the selected period",
}) => {
  const { resolvedTheme } = useTheme();
  const displayTimeZone = useDisplayTimeZone();
  const { displayCurrency } = useDisplayCurrency();
  const axisTickColor = useStatsCardHeadlineColor();
  const themeClass = resolvedTheme === "dark" ? "dark" : "";

  const chartData = useMemo((): EquityPeakChartPoint[] => {
    if (!drawdownData?.length) return [];
    const ymdBuckets = drawdownData
      .map((point) => (point.date.length >= 10 ? point.date.slice(0, 10) : point.date))
      .filter(Boolean);
    const includeYear = shouldIncludeYearOnChartAxis(ymdBuckets);

    return buildEquityPeakSeries(drawdownData, (ymd) =>
      formatYmdBucketShortInZone(ymd, displayTimeZone, { includeYear })
    );
  }, [drawdownData, displayTimeZone]);

  const headerValues = useMemo(() => {
    if (!chartData.length) {
      return { equity: "N/A", peak: "N/A" };
    }
    const last = chartData[chartData.length - 1];
    return {
      equity: formatDisplayCurrency(last.equity, displayCurrency, { locale: "en-US" }),
      peak: formatDisplayCurrency(last.peak, displayCurrency, { locale: "en-US" }),
    };
  }, [chartData, displayCurrency]);

  const seriesSummary = (
    <div className="flex w-full sm:w-auto">
      {(
        [
          { key: "equity" as const, color: EQUITY_LINE_COLOR },
          { key: "peak" as const, color: PEAK_LINE_COLOR },
        ] as const
      ).map(({ key, color }, index) => (
        <div
          key={key}
          className={cn(
            "flex min-w-[140px] flex-1 flex-col justify-center gap-1 border-t border-border px-[22px] py-4 text-left sm:border-t-0",
            index > 0 && "sm:border-l"
          )}
          style={{ fontFamily: STATS_FONT_FAMILY }}
        >
          <span className="text-xs text-muted-foreground">{chartConfig[key].label}</span>
          {loading ? (
            <Skeleton height="28px" width="100px" mt={1} borderRadius="md" />
          ) : (
            <span
              className="text-lg font-semibold leading-none tabular-nums tracking-tight"
              style={{ color }}
            >
              {headerValues[key]}
            </span>
          )}
        </div>
      ))}
    </div>
  );

  const chartContent = (
    <ChartPanel
      loading={loading}
      error={error}
      errorAction={errorAction}
      chartData={chartData}
      axisTickColor={axisTickColor}
      displayCurrency={displayCurrency}
      displayTimeZone={displayTimeZone}
    />
  );

  if (compact) {
    return chartContent;
  }

  if (embedded) {
    return (
      <div
        className={cn(themeClass, fillHeight && "flex h-full min-h-0 flex-col")}
        style={{ fontFamily: STATS_FONT_FAMILY }}
      >
        <div className="flex flex-col border-b border-border sm:flex-row">{seriesSummary}</div>
        <div
          className={cn(
            statisticsShadcnCardContentClassName,
            "pt-4",
            fillHeight && "flex min-h-0 flex-1 flex-col"
          )}
        >
          <div
            className={cn(
              statisticsShadcnBarChartAreaClassName,
              fillHeight && "flex min-h-0 flex-1 flex-col"
            )}
          >
            {chartContent}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card
      data-statistics-shadcn-card
      data-dashboard-chart-widget-fill={fillHeight ? "" : undefined}
      className={cn(
        statisticsShadcnCardClassName,
        themeClass,
        fillHeight && "h-full min-h-0 w-full flex-1"
      )}
      style={{ fontFamily: STATS_FONT_FAMILY }}
    >
      <CardHeader className="flex flex-col items-stretch border-b border-border !p-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1 px-[22px] py-4 sm:py-5">
          <StatisticsCardHeadline>{title}</StatisticsCardHeadline>
          <CardDescription className="text-xs" style={{ fontFamily: STATS_FONT_FAMILY }}>
            {description}
          </CardDescription>
        </div>
        {seriesSummary}
      </CardHeader>
      <CardContent
        className={cn(
          statisticsShadcnCardContentClassName,
          "pt-4",
          fillHeight && "flex min-h-0 flex-1 flex-col"
        )}
      >
        <div
          className={cn(
            statisticsShadcnBarChartAreaClassName,
            fillHeight && "flex min-h-0 flex-1 flex-col"
          )}
        >
          {chartContent}
        </div>
      </CardContent>
    </Card>
  );
};

function ChartPanel({
  loading,
  error,
  errorAction = null,
  chartData,
  axisTickColor,
  displayCurrency,
  displayTimeZone,
}: {
  loading: boolean;
  error: string | null;
  errorAction?: string | null;
  chartData: EquityPeakChartPoint[];
  axisTickColor: string;
  displayCurrency: string;
  displayTimeZone: string;
}) {
  const { showWidgetError, widgetError, widgetAction } = useStatisticsWidgetErrorGate(
    error,
    errorAction
  );
  // Recharts ResponsiveContainer needs a definite height; h-full inside dashboard flex
  // rows often resolves to 0 and hides the plot lines entirely.
  const chartContainerClassName = cn(
    "aspect-auto !h-[300px] min-h-[300px] w-full min-w-0",
    "[&_.recharts-responsive-container]:!h-[300px]",
    "[&_.recharts-wrapper]:!h-[300px] [&_.recharts-wrapper]:!overflow-visible",
    "[&_.recharts-surface]:overflow-visible"
  );
  const panelHeightProps = { h: "300px", minH: "300px", w: "100%" };

  if (loading) {
    return (
      <Box {...panelHeightProps}>
        <Skeleton height="100%" borderRadius="md" />
      </Box>
    );
  }

  if (showWidgetError && widgetError) {
    return <StatisticsInlineError message={widgetError} action={widgetAction ?? undefined} />;
  }

  if (chartData.length === 0) {
    return (
      <Box {...panelHeightProps} display="flex" alignItems="center" justifyContent="center">
        <Text fontSize="sm" color="gray.500" fontFamily={STATS_FONT_FAMILY}>
          {STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
        </Text>
      </Box>
    );
  }

  const formatMoney = (value: number) =>
    formatDisplayCurrency(value, displayCurrency, { locale: "en-US" });

  return (
    <Box {...panelHeightProps}>
      <ChartContainer
        config={chartConfig}
        className={chartContainerClassName}
        initialDimension={{ width: 640, height: 300 }}
        style={{ fontFamily: STATS_FONT_FAMILY, height: 300, minHeight: 300 }}
      >
        <ComposedChart
          accessibilityLayer
          data={chartData}
          margin={{
            top: 16,
            right: 24,
            left: 16,
            bottom: 20,
          }}
        >
        <CartesianGrid vertical={false} strokeDasharray="4 4" className="stroke-border/50" />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={32}
          interval="preserveStartEnd"
          tick={{ fill: axisTickColor, fontSize: 11 }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          width={72}
          tick={{ fill: axisTickColor, fontSize: 11 }}
          tickFormatter={(value: number) => formatMoney(value)}
        />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              className={statsChartTooltipContentClassName("w-[200px]")}
              labelClassName={STATS_CHART_TOOLTIP_TITLE_CLASS}
              labelFormatter={(_value, payload) => {
                const row = payload?.[0]?.payload as EquityPeakChartPoint | undefined;
                if (!row?.date) {
                  return row?.label ?? "";
                }
                const parsed = new Date(row.date);
                if (Number.isNaN(parsed.getTime())) {
                  return row.label;
                }
                return formatTradeDateOnlyForDisplay(parsed, displayTimeZone);
              }}
              formatter={(value, name) => {
                const n = Number(value);
                const key = name === "peak" ? "peak" : "equity";
                return (
                  <StatisticsChartTooltipRow
                    label={chartConfig[key].label}
                    value={formatMoney(n)}
                  />
                );
              }}
            />
          }
        />
        <Line
          dataKey="peak"
          type="monotone"
          stroke={PEAK_LINE_COLOR}
          strokeWidth={2}
          strokeDasharray="5 4"
          dot={false}
          isAnimationActive={false}
        />
        <Line
          dataKey="equity"
          type="monotone"
          stroke={EQUITY_LINE_COLOR}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ChartContainer>
    </Box>
  );
}

export default EquityPeakPathChart;
